import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, LinearProgress, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { ContentCopy as CopyIcon, Download as DownloadIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { calendarService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const INTEREST_LABEL = { oui: 'Oui', peut_etre: 'Peut-être', non: 'Non' };
const INTEREST_COLOR = { oui: 'success', peut_etre: 'warning', non: 'default' };
const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');
const pct = (n, total) => (total ? Math.round((n / total) * 100) : 0);

function Stat({ label, value, sub, color }) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, color }}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

function PriceBars({ title, region, data, options, currency }) {
  const counts = data.prices[region] || {};
  const max = Math.max(1, ...options.map((o) => counts[o.key] || 0));
  const answered = options.reduce((s, o) => s + (counts[o.key] || 0), 0);
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title} — {answered} réponse(s)</Typography>
        <Stack spacing={1.2} sx={{ mt: 1.5 }}>
          {options.map((o) => (
            <Box key={o.key}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">{o.label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{counts[o.key] || 0}</Typography>
              </Box>
              <LinearProgress variant="determinate" value={((counts[o.key] || 0) / max) * 100} sx={{ height: 8, borderRadius: 4 }} />
            </Box>
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Budget estimé ({currency}, au milieu de chaque tranche × quantité) : « Oui » {fmt(data.budget[region].oui)} · « Peut-être » {fmt(data.budget[region].peut_etre)}
        </Typography>
      </CardContent>
    </Card>
  );
}

function CalendarResults() {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const isSuperAdmin = user && user.role === 'super_admin';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const link = `${window.location.origin}/calendrier`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await calendarService.getResults();
      setData(res.data);
    } catch (err) {
      enqueueSnackbar('Erreur lors du chargement des réponses', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(link); enqueueSnackbar('Lien copié', { variant: 'success' }); } catch (_) { enqueueSnackbar(link, { variant: 'info' }); }
  };

  const exportCsv = async () => {
    try {
      const res = await calendarService.exportCsv();
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `calendrier-sondage-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      enqueueSnackbar('Erreur lors de l\'export', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Calendrier papier : sondage</Typography>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Actualiser</Button>
          {isSuperAdmin && <Button startIcon={<DownloadIcon />} variant="outlined" onClick={exportCsv}>Exporter (CSV)</Button>}
        </Stack>
      </Box>

      <Alert severity="info" action={<Button color="inherit" size="small" startIcon={<CopyIcon />} onClick={copyLink}>Copier</Button>}>
        Lien à mettre dans le message aux abonnés : <strong>{link}</strong>
      </Alert>

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : data && data.total === 0 ? (
        <Typography color="text.secondary">Aucune réponse pour le moment.</Typography>
      ) : data && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}><Stat label="Réponses" value={fmt(data.total)} sub={`${data.regions.cameroun} Cameroun · ${data.regions.etranger} étranger`} /></Grid>
            <Grid item xs={6} md={3}><Stat label="Oui, certainement" value={fmt(data.byInterest.oui)} sub={`${pct(data.byInterest.oui, data.total)} % · ${fmt(data.units.oui)} exemplaire(s)`} color="success.main" /></Grid>
            <Grid item xs={6} md={3}><Stat label="Peut-être" value={fmt(data.byInterest.peut_etre)} sub={`${pct(data.byInterest.peut_etre, data.total)} % · ${fmt(data.units.peut_etre)} exemplaire(s)`} color="warning.main" /></Grid>
            <Grid item xs={6} md={3}><Stat label="Non" value={fmt(data.byInterest.non)} sub={`${data.withContact} contact(s) laissé(s)`} /></Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}><PriceBars title="Cameroun" region="cameroun" data={data} options={data.options.cameroun.options} currency="FCFA" /></Grid>
            <Grid item xs={12} md={6}><PriceBars title="Étranger" region="etranger" data={data} options={data.options.etranger.options} currency="€" /></Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1 }}>Toutes les réponses</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Intérêt</TableCell>
                  <TableCell>Région</TableCell>
                  <TableCell>Prix</TableCell>
                  <TableCell align="right">Qté</TableCell>
                  <TableCell>Nom</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Ville</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.responses.map((r) => {
                  const opt = r.price_key ? data.options[r.region].options.find((o) => o.key === r.price_key) : null;
                  return (
                    <TableRow key={r.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(r.updated_at).toLocaleDateString('fr-FR')}</TableCell>
                      <TableCell><Chip size="small" label={INTEREST_LABEL[r.interest]} color={INTEREST_COLOR[r.interest]} /></TableCell>
                      <TableCell>{r.region === 'cameroun' ? 'Cameroun' : 'Étranger'}</TableCell>
                      <TableCell>{opt ? opt.label : '-'}</TableCell>
                      <TableCell align="right">{r.interest === 'non' ? '-' : r.quantity}</TableCell>
                      <TableCell>{r.contact_name || '-'}</TableCell>
                      <TableCell>{r.contact || '-'}</TableCell>
                      <TableCell>{r.city || '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}

export default CalendarResults;
